// crisisFloor.ts — the on-device mirror of the Ask ReNurture crisis floor.
//
// WHY A SECOND COPY. The authoritative floor lives on the server (server/src/lib/brain.js,
// the CRISIS array) and runs before anything else. But Ask reaches it over the network,
// and a person disclosing self-harm must not be met with "Could not reach the guide right
// now" because Replit was cold-starting or the train went into a tunnel. So the same
// patterns run here FIRST: risk is caught instantly, offline, and the disclosure never
// leaves the device.
//
// KEEPING THE TWO IN STEP. These patterns are a literal transcription of the server list.
// If you change one, change the other, and add the new phrasing to BOTH test batteries
// (server/test/brain.test.js and crisisFloor.test.ts) — they deliberately share phrasings
// so drift shows up as a failure rather than as a missed disclosure.
//
// Deliberately regex-only, no model and no network: this floor has to work when every
// vendor is down. Direction matters — "harm MYSELF" always fires, a bare "harmed me" does
// not (that is usually the ex, historically), and "threatened to take me to court" stays a
// legal question rather than triggering a crisis screen.

const SELF = 'myself|my ?self';

const CRISIS: RegExp[] = [
  // Suicidal intent and ideation
  /suicid/i,
  new RegExp(`\\bkill(ing)?\\s+(${SELF}|me)\\b`, 'i'),
  /\btak(e|ing) my (own )?life\b/i,
  /\bend(ing)? (it all|my life|my own life|things)\b/i, // NOT a bare "end it" — that is usually the relationship
  /\b(want|wanting|wish|wished)\w*\s+(to die|i (was|were) dead|to be dead)/i,
  /\b(don'?t|do not|dont)\s+want to\s+(live|be here|be alive|wake up|exist|go on)\b/i,
  /\b(no (reason|point) (to|in) (live|living|going on)|nothing to live for)\b/i,
  /\bbetter off (dead|without me)\b/i,
  // Self-directed harm: verb + reflexive object, plus the compounds
  new RegExp(`\\b(harm|harming|hurt|hurting|injur\\w*|cut|cutting|burn|burning|damag\\w*|punish|punishing)\\s+(${SELF})\\b`, 'i'),
  /\bself[\s-]?(harm|harming|injur\w*|destruct\w*|mutilat\w*)/i,
  /\boverdos(e|ing)\b/i,
  // Threats and immediate danger from the ex
  /\bhurt me\b/i,
  /\bthreaten(s|ed|ing)?\s+(me|us|my (life|safety|children|kids|family|home))\b/i,
  /\bthreaten(s|ed|ing)?\s+to\s+(kill|hurt|harm|beat|attack|find|smash|burn|come)/i,
  /\b(death threats?|making threats|violent)\b/i,
  /\b(stalk(s|ed|ing)?|following)\s+me\b/i,
  /\b(has|had|got|with)\s+a\s+(knife|weapon|gun)\b/i,
  /he'?s (here|outside|at the door) (now|right now)/i,
  /\b(on (his|her|their) way|coming)\s+(to (my|the) (house|home|door|work|flat)|here|round)\b/i,
  /going to (kill|hurt) me/i,
  /\bin danger\b/i,
  /\bat risk\b/i,
  /(feel|feeling|am|i'?m)\s+unsafe/i,
  /\bnot safe\b/i,
  /(scared|frightened|terrified) (for|of) (my|our) (life|lives|safety)/i,
];

/** True when the words read as risk of harm (to self, or from the ex) and the guide
 *  must show real support rather than content. Mirrors the server floor exactly. */
export const isCrisis = (q: string): boolean => CRISIS.some((re) => re.test(q || ''));
